import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import axios from 'axios';

const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://moga-dev.com/api';

// Get user orders
export const fetchUserOrders = createAsyncThunk(
    'orders/fetchUserOrders',
    async (_, { rejectWithValue, getState }) => {
        try {
            const token = getState().auth.token;
            const response = await axios.get(`${baseUrl}/orders`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data || [];
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to fetch orders');
        }
    }
);

// Delete order
export const deleteOrder = createAsyncThunk(
    'orders/deleteOrder',
    async (orderId, { rejectWithValue, getState }) => {
      try {
        const token = getState().auth.token;
        await axios.post(
          `${baseUrl}/orders/${orderId}/cancel`,
          {}, // empty body
          {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        return orderId;
      } catch (error) {
        return rejectWithValue(error.code || error.message || 'Failed to delete order');
      }
    }
  );

// Create new order
export const createOrder = createAsyncThunk(
    'orders/createOrder',
    async (orderData, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append('user_id', orderData.user_id);

            orderData.items.forEach((item, index) => {
                formData.append(`items[${index}][product_id]`, item.product_id);
                if (item.card_id) formData.append(`items[${index}][card_id]`, item.card_id);
                if (item.message) formData.append(`items[${index}][message]`, item.message);
                if (item.link) formData.append(`items[${index}][link]`, item.link);
                if (item.upload_video) {
                    formData.append(`items[${index}][upload_video]`, item.upload_video);
                }
                if (item.record_video) {
                    formData.append(`items[${index}][record_video]`, item.record_video);
                }
                if (item.addons && item.addons.length > 0) {
                    item.addons.forEach((addon, addonIndex) => {
                        formData.append(`items[${index}][addons][${addonIndex}][addons_id]`, addon.addons_id);
                        formData.append(`items[${index}][addons][${addonIndex}][quantity]`, addon.quantity);
                    });
                }
            });

            const response = await axios.post(`${baseUrl}/orders`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to create order');
        }
    }
);



// Initial state
const initialState = {
    orders: [],
    loading: false,
    error: null,
};

// Orders slice
const ordersSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        resetOrders: (state) => {
            state.orders = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch orders
            .addCase(fetchUserOrders.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserOrders.fulfilled, (state, action) => {
                state.loading = false;
                if (Array.isArray(action.payload?.Data)) {
                    state.orders = action.payload.Data; // Could be empty, that's fine
                    state.error = null;
                } else {
                    state.orders = [];
                    state.error = "Failed to load orders"; // fallback if Data is not an array
                }
            })
            .addCase(fetchUserOrders.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Create order
            .addCase(createOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createOrder.fulfilled, (state, action) => {
                state.loading = false;
                state.orders.unshift(action.payload.Data);
            })
            .addCase(createOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Delete order
            .addCase(deleteOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteOrder.fulfilled, (state, action) => {
                state.loading = false;
                state.orders = state.orders.filter(order => order.id !== action.payload);
            })
            .addCase(deleteOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export default ordersSlice.reducer;
export const { resetOrders } = ordersSlice.actions;

// Selectors
export const selectOrders = (state) => state.orders.orders;
export const selectOrdersLoading = (state) => state.orders.loading;
export const selectOrdersError = (state) => state.orders.error;

